import React, { useState } from "react";
import { useL } from "@/lib/useL";
import { Play, X, ArrowRight } from "lucide-react";
import { trackEvent, logFunnel } from "@/lib/analytics";
import { renderSummary } from "@/lib/renderSummary";
import { INSTANT_EXAMPLES } from "@/lib/instantExamples";
import { formatLabel } from "@/lib/formatLabels";

const chip = (active) => `px-3 py-1.5 text-xs font-mono uppercase tracking-[0.1em] border transition-colors ${active ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border hover:border-[#002FA7]"}`;

const Summary = ({ text }) => (
  <div data-testid="instant-example-summary" className="summary-content notranslate md:max-h-[420px] md:overflow-y-auto pr-2 text-[14px]"
    dangerouslySetInnerHTML={{ __html: renderSummary(text) }} />
);

const Settings = ({ s, L, en }) => {
  const rows = [[L("Modèle", "Model"), s.model], [L("Longueur", "Length"), s.length[en]], [L("Format", "Format"), formatLabel(s.format, en)], [L("Niveau", "Level"), s.level[en]]];
  return (
    <div data-testid="instant-example-settings" className="mt-4 pt-3 border-t border-[#002FA7]/20">
      <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mb-2">{L("Réglages utilisés", "Settings used")}</div>
      <div className="flex flex-wrap gap-1.5">
        {rows.map(([k, v]) => <span key={k} className="text-[11px] border border-border bg-background px-2 py-1"><span className="text-muted-foreground">{k}{L(" :", ":")}</span> <b>{v}</b></span>)}
      </div>
    </div>
  );
};

export function InstantExample({ anonymous, onTryReal, onUseOwn }) {
  const L = useL();
  const en = L(0, 1);
  const [open, setOpen] = useState(false);
  const [ti, setTi] = useState(0);
  const [vi, setVi] = useState(0);
  const [full, setFull] = useState(false);
  const ex = INSTANT_EXAMPLES[ti];
  const v = ex.variants[vi];
  const total = INSTANT_EXAMPLES.reduce((n, e) => n + e.variants.length, 0);

  const show = () => { setOpen(true); trackEvent("instant_example_viewed"); logFunnel("example_view"); };

  if (!open) {
    return (
      <div className="mx-6 md:mx-8 lg:mx-10 mt-6">
        <button type="button" onClick={show} data-testid="instant-example-btn"
          className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#002FA7] text-white px-6 py-4 font-black tracking-tight hover:bg-[#00237d] transition-colors" style={{ fontFamily: "Outfit" }}>
          <Play className="w-4 h-4 fill-current" />
          {L("Essayer avec un exemple", "Try with an example")}
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] opacity-80">
            · {total} {L("résumés instantanés", "instant summaries")}{anonymous ? L(", sans compte", ", no account") : ""}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="mx-6 md:mx-8 lg:mx-10 mt-6 border-2 border-[#002FA7] animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="instant-example-card">
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border">
        <div className="flex flex-wrap gap-2">
          {INSTANT_EXAMPLES.map((e, i) => (
            <button key={e.id} type="button" onClick={() => { setTi(i); setVi(0); setFull(false); }} className={chip(i === ti)} data-testid={`instant-example-text-${e.id}`}>{e.tag[en]}</button>
          ))}
        </div>
        <button type="button" onClick={() => setOpen(false)} aria-label={L("Fermer", "Close")} data-testid="instant-example-close" className="text-muted-foreground hover:text-foreground shrink-0"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid md:grid-cols-2">
        <div className="p-5 md:border-r border-border">
          <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mb-2">{L("Texte d'origine", "Original text")} · {ex.source[en].length} {L("caractères", "chars")}</div>
          <p className={`text-[13px] leading-relaxed text-muted-foreground ${full ? "" : "line-clamp-4 md:line-clamp-none"}`} data-testid="instant-example-source">{ex.source[en]}</p>
          {!full && <button type="button" onClick={() => setFull(true)} data-testid="instant-example-source-more" className="md:hidden mt-1 text-xs text-[#002FA7] underline">{L("Voir tout le texte", "Show full text")}</button>}
        </div>
        <div className="p-5 bg-[#002FA7]/[0.04]">
          <div className="flex flex-wrap gap-2 mb-4">
            {ex.variants.map((x, i) => (
              <button key={x.settings.format} type="button" onClick={() => setVi(i)} className={chip(i === vi)} data-testid={`instant-example-variant-${i}`}>
                {L("Résumé", "Summary")} {i + 1} · {formatLabel(x.settings.format, en)}
              </button>
            ))}
          </div>
          <Summary text={v.summary[en]} />
          <Settings s={v.settings} L={L} en={en} />
        </div>
      </div>
      <div className="flex flex-wrap gap-3 px-5 py-4 border-t border-border">
        <button type="button" onClick={onUseOwn} data-testid="instant-example-use-own"
          className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#00237d] transition-colors">
          {L("À vous : collez votre texte", "Your turn: paste your text")} <ArrowRight className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onTryReal(ex.source[en])} data-testid="instant-example-try-real"
          className="px-5 py-2.5 text-sm border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors">
          {L("Résumer ce texte en direct", "Summarise this text live")}
        </button>
      </div>
    </div>
  );
}
